import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "@src/styles/style.sass";
import "@src/styles/themes/dark.sass";
import "@src/styles/themes/light.sass";
import { createBrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider } from "react-router";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import UserConfirm from "./pages/users/Confirm";
import PrivateRoute from "./utils/PrivateRoute";
import Logout from "./pages/Logout";
import UserEdit from "./pages/users/edit";
import ConfirmEmail from "./components/ConfirmEmail/ConfirmEmail";
import CharactersIndex from "./pages/characters";
import CharactersShow from "./pages/characters/show";
import CharactersNew from "./pages/characters/new";
import Error from "./layout/Error";
import NotFound from "./layout/NotFound";
import Homepage from "./pages/Homepage";
import HeightChartPage from "./pages/characters/heightchart";
import LinksIndex from "./pages/links";
import Feedback from "./pages/Feedback";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { TermsAndConditons } from "./pages/TermsAndConditions";
import { WorldIndex } from "./pages/worlds";
import { WorldShow } from "./pages/worlds/show";
import { WorldNew } from "./pages/worlds/new";
import { WorldEdit } from "./pages/worlds/edit";

const router = createBrowserRouter([
	{
		path: "/",
		element: <App />,
		errorElement: <Error />,
		children: [
			{
				path: "",
				element: <Homepage />,
			},
			{
				path: "login",
				element: <Login />,
			},
			{ path: "logout", element: <Logout /> },
			{
				path: "register",
				element: <Register />,
			},
			{
				path: "feedback",
				element: <Feedback />,
			},
			{
				path: "privacy-policy",
				element: <PrivacyPolicy />,
			},
			{
				path: "terms-and-conditions",
				element: <TermsAndConditons />,
			},
			{
				path: "reset-password/:resetToken?",
				element: <ResetPassword />,
			},
			{
				path: "confirm-email/:token",
				element: <UserConfirm />,
			},
			{
				element: <PrivateRoute />,
				children: [
					{
						path: "heightchart",
						element: <HeightChartPage />,
					},
					{
						path: "users",
						children: [
							{
								path: "settings",
								element: <UserEdit />,
								children: [
									{
										path: ":token",
										element: <ConfirmEmail />,
									},
								],
							},
						],
					},
					{
						path: "characters",
						children: [
							{
								path: "",
								element: <CharactersIndex />,
							},
							{
								path: ":id",
								element: <CharactersShow />,
							},
							{ path: "new", element: <CharactersNew /> },
						],
					},
					{
						path: "worlds",
						children: [
							{
								path: "",
								element: <WorldIndex />,
							},
							{
								path: ":id",
								element: <WorldShow />,
							},
							{
								path: "edit/:id",
								element: <WorldEdit />,
							},
							{ path: "new", element: <WorldNew /> },
						],
					},

					{
						path: "links",
						children: [
							{
								path: "",
								element: <LinksIndex />,
							},
						],
					},
				],
			},
			{
				path: "*",
				element: <NotFound />,
			},
		],
	},
]);

const root = ReactDOM.createRoot(document.getElementById("root")!);

const queryClient = new QueryClient({
	defaultOptions: { queries: { retry: false } },
});
Sentry.init({
	dsn: import.meta.env.VITE_SENTRY_DNS_FRONT,
});
root.render(
	<React.StrictMode>
		<QueryClientProvider client={queryClient}>
			<RouterProvider router={router} />

			<ReactQueryDevtools initialIsOpen={true} />
		</QueryClientProvider>
	</React.StrictMode>,
);
